// オンライン対戦の部屋へつなぐ（一覧の側）。ゲーム（iframe）は外へ通信できないので、ここが代わりにつなぐ。
// 流れ：待合室（lobby）に WebSocket でつなぎ、相手が揃ったら部屋の番号をもらう → 部屋（room）につなぎ直す。
// ログインのトークンは副プロトコルに載せる（URL に載せない）。相手の uid はここに持ち、ゲームには渡さない。
import type { Json, JoinResult, OnlinePlayer, OnlineRoom, StampId } from "@appful/sdk/protocol";
import { auth } from "./firebase";
import { config } from "./config";

const SUBPROTOCOL = "appful-rooms.v1";
const RESULT_TIMEOUT_MS = 15_000;

export interface RoomPlayer extends OnlinePlayer {
  /** ブロックと通報に使う。ゲームには渡さない */
  uid: string;
}

export interface OnlineHooks {
  /** ゲームへ知らせる出来事 */
  message(from: number, data: Json): void;
  player(seat: number, connected: boolean): void;
  host(seat: number): void;
  stamp(from: number, id: StampId): void;
  closed(reason: "left" | "timeout" | "server"): void;
  /** 部屋の顔ぶれが変わった（一覧の帯を描き直す） */
  roster(players: RoomPlayer[], me: number): void;
}

type ServerMsg =
  | { t: "waiting" }
  | { t: "matched"; room: string }
  | { t: "start"; me: number; host: number; mode: string; players: RoomPlayer[] }
  | { t: "msg"; from: number; d: Json }
  | { t: "player"; seat: number; connected: boolean }
  | { t: "host"; seat: number }
  | { t: "stamp"; from: number; id: StampId }
  | { t: "result"; agreed: boolean; placements: number[] | null }
  | { t: "closed"; reason: "timeout" | "server" };

async function open(path: string): Promise<WebSocket> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error("not signed in");
  return new WebSocket(`${config.roomsOrigin}${path}`, [SUBPROTOCOL, `auth.${token}`]);
}

export class OnlineClient {
  private lobby: WebSocket | null = null;
  private room: WebSocket | null = null;
  private roomId: string | null = null;
  private cancelJoin: (() => void) | null = null;
  private pendingResult: ((r: { agreed: boolean; placements: number[] | null }) => void) | null = null;
  private leaving = false;
  private reconnects = 0;
  players: RoomPlayer[] = [];
  me = -1;

  constructor(private gameId: string, private hooks: OnlineHooks) {}

  get inRoom() {
    return this.room !== null && this.me >= 0;
  }

  /** 相手を探して部屋に入る。blocks はこの人がブロックした相手の uid */
  async join(mode: string, blocks: string[]): Promise<JoinResult> {
    this.leave();
    this.leaving = false;
    let roomId: string;
    try {
      const r = await this.waitInLobby(mode, blocks);
      if (r === null) return { status: "cancelled" };
      roomId = r;
    } catch (e) {
      console.warn("[online] lobby", e);
      return { status: "unavailable" };
    }
    try {
      const room = await this.enterRoom(roomId);
      return { status: "matched", room };
    } catch (e) {
      console.warn("[online] room", e);
      this.leave();
      return { status: "unavailable" };
    }
  }

  private async waitInLobby(mode: string, blocks: string[]): Promise<string | null> {
    const ws = await open(`/v1/lobby?game=${encodeURIComponent(this.gameId)}&mode=${encodeURIComponent(mode)}`);
    this.lobby = ws;
    return new Promise((resolve, reject) => {
      let matched: string | null = null;
      this.cancelJoin = () => {
        ws.close(1000, "cancel");
        resolve(null);
      };
      ws.onopen = () => ws.send(JSON.stringify({ t: "join", blocks }));
      ws.onmessage = (ev) => {
        const m = JSON.parse(String(ev.data)) as ServerMsg;
        if (m.t === "matched") matched = m.room;
      };
      ws.onclose = () => {
        this.lobby = null;
        this.cancelJoin = null;
        if (matched) resolve(matched);
        else reject(new Error("lobby closed"));
      };
    });
  }

  private async enterRoom(roomId: string): Promise<OnlineRoom> {
    const ws = await open(`/v1/room/${roomId}`);
    this.room = ws;
    this.roomId = roomId;
    return new Promise((resolve, reject) => {
      let started = false;
      this.cancelJoin = () => {
        ws.close(1000, "cancel");
        reject(new Error("cancelled"));
      };
      ws.onmessage = (ev) => {
        const m = JSON.parse(String(ev.data)) as ServerMsg;
        if (m.t === "start") {
          started = true;
          this.cancelJoin = null;
          this.reconnects = 0;
          this.me = m.me;
          this.players = m.players;
          this.hooks.roster(this.players, this.me);
          resolve({ me: m.me, host: m.host, mode: m.mode, players: m.players.map(({ uid: _uid, ...p }) => p) });
          return;
        }
        this.onRoomMessage(m);
      };
      ws.onclose = () => {
        if (this.room === ws) this.room = null;
        if (!started) return reject(new Error("room closed before start"));
        if (this.leaving) return;
        // 途切れたら1回だけつなぎ直す（部屋は顔ぶれの人を待っている）
        if (this.reconnects++ < 1 && this.roomId) {
          setTimeout(() => {
            if (this.leaving || !this.roomId) return;
            this.enterRoom(this.roomId).catch(() => this.hooks.closed("server"));
          }, 1_000);
        } else {
          this.hooks.closed("server");
        }
      };
    });
  }

  private onRoomMessage(m: ServerMsg) {
    switch (m.t) {
      case "msg":
        return this.hooks.message(m.from, m.d);
      case "player": {
        const p = this.players.find((x) => x.seat === m.seat);
        if (p) p.connected = m.connected;
        this.hooks.roster(this.players, this.me);
        return this.hooks.player(m.seat, m.connected);
      }
      case "host":
        return this.hooks.host(m.seat);
      case "stamp":
        return this.hooks.stamp(m.from, m.id);
      case "result":
        this.pendingResult?.({ agreed: m.agreed, placements: m.placements });
        this.pendingResult = null;
        return;
      case "closed":
        this.leaving = true;
        return this.hooks.closed(m.reason);
    }
  }

  send(data: Json, to?: number) {
    if (this.room?.readyState === WebSocket.OPEN) this.room.send(JSON.stringify(to === undefined ? { t: "msg", d: data } : { t: "msg", d: data, to }));
  }

  stamp(id: StampId) {
    if (this.room?.readyState === WebSocket.OPEN) this.room.send(JSON.stringify({ t: "stamp", id }));
  }

  result(placements: number[]): Promise<{ agreed: boolean; placements: number[] | null }> {
    if (this.room?.readyState !== WebSocket.OPEN) return Promise.resolve({ agreed: false, placements: null });
    this.pendingResult?.({ agreed: false, placements: null });
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.pendingResult = null;
        resolve({ agreed: false, placements: null });
      }, RESULT_TIMEOUT_MS);
      this.pendingResult = (r) => {
        clearTimeout(timer);
        resolve(r);
      };
      this.room!.send(JSON.stringify({ t: "result", placements }));
    });
  }

  /** 探すのをやめる・部屋を出る */
  leave() {
    this.leaving = true;
    this.cancelJoin?.();
    this.cancelJoin = null;
    this.lobby?.close(1000, "leave");
    if (this.room?.readyState === WebSocket.OPEN) this.room.send(JSON.stringify({ t: "leave" }));
    this.room?.close(1000, "leave");
    this.lobby = null;
    this.room = null;
    this.roomId = null;
    this.pendingResult?.({ agreed: false, placements: null });
    this.pendingResult = null;
    if (this.me >= 0) {
      this.me = -1;
      this.players = [];
      this.hooks.roster([], -1);
    }
  }
}
